// src/components/tokenPage/derivatives/FundingExtremesCard.tsx

import React, { useState } from "react";
import TimeRangePicker, { type TimeRangeOption } from "@/components/common/TimeRangePicker";
import { useTokenDerivatives } from "@/contexts/token";
import { DataCard } from "@/components/ui";
import { formatDateLabel } from "@/utils/charts/dataUtils";

interface Props {
  ticker: string;
}

type WindowKey = '7d' | '30d' | '180d' | '365d';

const FundingExtremesCard: React.FC<Props> = () => {
  const extremesRangeOptions: readonly TimeRangeOption[] = ['7d','30d','180d','365d'];
  const [extremesRange, setExtremesRange] = useState<TimeRangeOption>('7d');
  
  const { fundingOiWeighted, isLoading } = useTokenDerivatives();

  // Get pre-computed extremes for selected window
  const windowKey = extremesRange as WindowKey;
  const { top, bottom } = fundingOiWeighted.extremes[windowKey];
  
  // Hide if no data available (after loading)
  if (!isLoading && top.length === 0 && bottom.length === 0) {
    return null;
  }

  const fmtDate = (t: number) => formatDateLabel(t, 'medium');
  const fmtPct = (v: number) => {
    const abs = Math.abs(v);
    const decimals = abs < 0.01 ? 4 : 3;
    return v.toFixed(decimals) + '%';
  };

  return (
    <DataCard
      title="Funding Extremes (OI‑weighted)"
      isLoading={isLoading}
      loadingVariant="stats"
      actions={
        <TimeRangePicker options={extremesRangeOptions} value={extremesRange} onChange={setExtremesRange} theme="blue" />
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-[11px] text-slate-400 mb-1">Top</div>
          <div className="space-y-1">
            {top.map((x, idx) => (
              <div key={`top-${idx}`} className="flex items-center justify-between text-sm">
                <span className="text-slate-300">{fmtDate(x.timestamp)}</span>
                <span className="tabular-nums text-emerald-400">{fmtPct(x.rate)}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[11px] text-slate-400 mb-1">Bottom</div>
          <div className="space-y-1">
            {bottom.map((x, idx) => (
              <div key={`bot-${idx}`} className="flex items-center justify-between text-sm">
                <span className="text-slate-300">{fmtDate(x.timestamp)}</span>
                <span className="tabular-nums text-rose-400">{fmtPct(x.rate)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DataCard>
  );
};

export default FundingExtremesCard;
